<template>
  <div id="app" v-bind:class="{ 'no-nav': !isNavigationAvailable }">
    <div id="nav" v-if="isNavigationAvailable">
      <div class="main-nav">
        <div class="nav-card">
          <router-link to="/" exact>
            <ifield-logo class="ifield-nav-logo" />
          </router-link>
        </div>
        <div class="nav-card">
          <router-link to="/member">
            <p v-t="'route.member'" class="nav-text"></p>
          </router-link>
        </div>
        <div class="nav-card">
          <router-link to="/list">
            <p v-t="'route.list'" class="nav-text"></p>
          </router-link>
        </div>
        <div v-if="isReportAvailable" class="nav-card">
          <router-link to="/report">
            <p v-t="'route.report'" class="nav-text"></p>
          </router-link>
        </div>
        <div v-if="isPlaceAvailable" class="nav-card">
          <router-link to="/place">
            <p v-t="'route.place'" class="nav-text"></p>
          </router-link>
        </div>
        <div v-if="isStatusAvailable" class="nav-card">
          <router-link to="/status">
            <p v-t="'route.status'" class="nav-text"></p>
          </router-link>
        </div>
        <div v-if="isCheckinAvailable" class="nav-card">
          <router-link to="/checkin">
            <p v-t="'route.checkin'" class="nav-text"></p>
          </router-link>
        </div>
        <div v-if="isMessageAvailable" class="nav-card">
          <router-link to="/message">
            <p v-t="'route.message'" class="nav-text"></p>
            <span v-show="messageCount" class="nav-badge">
              {{ messageCount }}
            </span>
          </router-link>
        </div>
        <div v-if="isAnalyzeAvailable" class="nav-card">
          <router-link to="/analyze">
            <p v-t="'route.analyze'" class="nav-text"></p>
          </router-link>
        </div>
        <div v-if="$store.state.login.useSample" class="nav-card">
          <router-link to="/sample">
            <p v-t="'route.sample'" class="nav-text"></p>
          </router-link>
        </div>
        <!-- h
          [カスタマイズページ テンプレート]
          機能のOn/Offが必要であればstoreにフラグを定義して制御してください

        <div v-if="$store.state.login.useXXXXX">
          <router-link to="/xxxx">XXXXX</router-link>
        </div>
        -->
      </div>
      <div class="account-nav">
        <my-account
          ref="my-account"
          v-bind:userId="$store.state.login.loginCode"
          v-on:myaccount-click-profile="
            $store.dispatch('updateProfileUserId', $event.id)
          "
          v-on:myaccount-click-login-history="$_showLoginHistory"
          v-on:myaccount-click-setting="$_showSetting"
          v-on:myaccount-click-appinfo="$_showAppInfo"
          v-on:myaccount-click-change-password="$_showPasswordEditor"
        />
        <member-preference ref="memberPreference" />
        <application-information ref="applicationInformation" />
        <login-history ref="login-history" />
        <member-password-editor ref="memberPasswordEditor" />
      </div>
    </div>
    <keep-alive v-bind:include="keepAliveViewNames" v-bind:max="keepAliveMax">
      <router-view />
    </keep-alive>

    <member-profile ref="memberProfile" />
    <!-- TODO: 実装配置方法は変えるかも -->
    <map-layer-controller use-global-render-state v-if="isAppReady" />
  </div>
</template>
<script>
import { provide } from 'vue'
import myAccount from '@/components/AppMyAccount.vue'
import MapLayerController from '@/components/map/AppMapLayerController.vue'
import MemberProfile from '@/views/TheMemberProfile.vue'
import MemberPreference from '@/views/TheMemberPreference.vue'
import ApplicationInformation from '@/views/TheApplicationInformation.vue'
import LoginHistory from '@/views/TheLoginHistory.vue'
import MemberPasswordEditor from '@/views/TheMemberPasswordEditor.vue'
import {
  FUNCTION_TYPE,
  KEEP_ALIVE_MAX,
  KEEP_ALIVE_VIEW_NAMES,
  MAX_SAFE_INTERVAL_DELAY,
} from '@/constants.js'
import { RestApiClient } from 'ifield-core-components'
import { useBaseDate } from '@/composables/UseBaseDate.js'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { useInitializeAtLoggedIn } from '@/composables/UseInitializeAtLoggedIn.js'
import { toServerTz } from '@/js/TimezoneHelper'
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'
import ifieldLogo from '@/assets/ifield/ic_ifield.svg?component'

export default {
  components: {
    'my-account': myAccount,
    memberProfile: MemberProfile,
    memberPreference: MemberPreference,
    'application-information': ApplicationInformation,
    LoginHistory,
    MemberPasswordEditor,
    'map-layer-controller': MapLayerController,
    'ifield-logo': ifieldLogo,
  },
  setup(props, context) {
    const { handleApiError, showAuthenticationFailedAlert } = useHandleApiError(
      props,
      context
    )

    // 自動更新される baseDate をアプリケーション全体に provide
    const { baseDate } = useBaseDate()
    provide('baseDate', baseDate)

    const { onLoadAtLoggedIn } = useInitializeAtLoggedIn()
    return { handleApiError, showAuthenticationFailedAlert, onLoadAtLoggedIn }
  },
  data: function () {
    return {
      accountProfile: [],
      /**
       * メッセージ件数
       * @type {number}
       */
      messageCount: null,
      /**
       * メッセージチェックのインターバル処理ID
       * @type {number}
       */
      messageCheckIntervalId: null,
    }
  },
  computed: {
    ...mapState({
      /**
       * メッセージの定期チェックの基準日
       * @type {Date}
       */
      messageBaseDate: (state) => state.app.message.checkBaseDate,
      /**
       * メッセージ定期チェック間隔をミリ秒で取得する
       * @type {number}
       */
      messageCheckIntervalTime: (state) => {
        const interval = state.receiveMessage.updateInterval * 1000
        return interval > MAX_SAFE_INTERVAL_DELAY
          ? MAX_SAFE_INTERVAL_DELAY
          : interval
      },
      /**
       * ユーザー詳細を表示するユーザーID
       * @type {number}
       */
      profileUserId: (state) => state.app.user.profileUserId,
      /**
       * ログイン状態
       * @type {Boolean}
       */
      isAppReady: (state) => state.app.isReady,
    }),
    ...mapGetters(['isLoggedIn']),
    /**
     * 保持するページのコンポーネント名
     * ログアウトしたら空にして、保持しているページを破棄する
     * @type {String[]}
     */
    keepAliveViewNames: function () {
      return this.isLoggedIn ? KEEP_ALIVE_VIEW_NAMES : []
    },
    /**
     * 同時に保持するページ数の上限
     * @type {Number}
     */
    keepAliveMax: function () {
      return KEEP_ALIVE_MAX
    },
    /**
     * ナビゲーションバーを表示可能か？
     * @type {Boolean}
     */
    isNavigationAvailable: function () {
      return this.$route.meta.requiresAuth && this.isAppReady
    },
    /**
     * 報告書が利用可能か？
     * @type {Boolean}
     */
    isReportAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.REPORT)
    },
    /**
     * プレイスが利用可能か？
     * @type {Boolean}
     */
    isPlaceAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.PLACE)
    },
    /**
     * ステイタスが利用可能か？
     * @type {Boolean}
     */
    isStatusAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.STATUS)
    },
    /**
     * チェックインが利用可能か？
     * @type {Boolean}
     */
    isCheckinAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.CHECKIN)
    },
    /**
     * メッセージが利用可能か？
     * @type {Boolean}
     */
    isMessageAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.SEND_MESSAGE)
    },
    /**
     * 分析が利用可能か？
     * @type {Boolean}
     */
    isAnalyzeAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.ANALYZE)
    },
    /**
     * メッセージの定期チェックが可能な場合はtrue(メッセージ利用権限考慮)
     */
    canMessageCheck: function () {
      return (
        this.isMessageAvailable && this.$store.state.app.message.enabledCheck
      )
    },
    errorCode: function () {
      return this.$store.state.error.code
    },
  },
  watch: {
    errorCode: {
      handler: async function (val) {
        if (val !== 401) return
        if (!this.isLoggedIn) return

        this.$_stopCheckMessage()
        // 認証エラーの場合
        const result = await this.showAuthenticationFailedAlert()
        if (result) {
          // 認証情報を削除し、ログイン画面へ移動
          // ログアウトは一応送っているだけで、認証自体は既に切れているのでログイン画面遷移の前に成功を待つ必要はない
          RestApiClient.executePostApi(
            RestApiClient.createApiUrl('logout')
          ).catch((error) => {
            this.handleApiError(error, null)
          })
          this.$store.dispatch('logout')
          this.$router.push({
            path: 'login',
            query: { redirect: this.$route.path },
          })
        }
      },
      immediate: true,
    },
    $route() {
      this.$store.commit('setErrorCode', null)
    },
    messageCheckIntervalTime: function () {
      // 定期チェック処理を再登録する
      if (this.canMessageCheck) {
        this.$_stopCheckMessage()
        this.$_startCheckMessage()
      }
    },
    canMessageCheck: function () {
      if (this.canMessageCheck) {
        this.$_startCheckMessage()
      } else {
        this.$_stopCheckMessage()
        this.messageCount = null
      }
    },
    profileUserId: function (value) {
      value && this.$_showProfile(value)
    },
  },
  created: function () {
    if (this.isLoggedIn) {
      this.onLoadAtLoggedIn()
    }
  },
  mounted: function () {
    // メッセージ定期チェック
    if (this.canMessageCheck) {
      this.$_startCheckMessage()
    }
  },
  beforeDestroy: function () {
    // チェック処理を停止する
    this.$_stopCheckMessage()
  },
  methods: {
    /**
     * メンバープロフィールを表示する
     * @param {Number} id ユーザーID
     */
    $_showProfile: function (id) {
      this.$refs['memberProfile'].showModal(id)
    },
    /**
     * ログイン履歴を表示する
     */
    $_showLoginHistory: function () {
      this.$refs['login-history'].showModal()
    },
    /**
     * 個人設定を表示する
     */
    $_showSetting: function () {
      this.$refs['memberPreference'].showModal()
    },
    /**
     * アプリ情報を表示する
     */
    $_showAppInfo: function () {
      this.$refs['applicationInformation'].showModal()
    },
    /**
     * パスワード変更モーダルを表示する
     * @param {Object} user ユーザーデータ
     */
    $_showPasswordEditor: function (user) {
      this.$refs['memberPasswordEditor'].showModal(user)
    },
    /**
     * メッセージ件数を取得して表示する
     */
    $_showMessageCount: async function () {
      const url = RestApiClient.createApiUrl('receive_message/count')
      let params = url.searchParams
      params.append('read_flg', 0) // 未読のみ
      if (this.messageBaseDate != null) {
        const afterDate = toServerTz(
          +moment(this.messageBaseDate),
          'YYYY-MM-DDTHH:mm:ss.SSS'
        )
        params.append('after', afterDate)
      }

      try {
        const json = await RestApiClient.executeGetApi(url)
        this.messageCount = json.data.count
      } catch (error) {
        this.handleApiError(error)
      }
    },
    /**
     * メッセージチェックを開始する
     */
    $_startCheckMessage: function () {
      // 既に定期チェックが行われている時な何もしない
      if (this.messageCheckIntervalId != null) {
        return
      }

      // 初回分のチェックを行う
      this.$_showMessageCount()

      // 定期チェックを開始する
      this.messageCheckIntervalId = setInterval(
        this.$_showMessageCount,
        this.messageCheckIntervalTime
      )
      this.$log.debug('Message checking has started.')
    },
    /**
     * メッセージチェックを停止する
     */
    $_stopCheckMessage: function () {
      if (this.messageCheckIntervalId != null) {
        clearInterval(this.messageCheckIntervalId)
        this.messageCheckIntervalId = null
        this.$log.debug('Message checking has stopped.')
      }
    },
  },
}
</script>

<style lang="scss">
#app {
  /*  font-family: 'Avenir', Helvetica, Arial, sans-serif; */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: $text-color;
  max-width: 100%;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: $navigation-bar-height minmax(0, 1fr);
  overflow: hidden;

  &.no-nav {
    display: block;
  }
}

#nav {
  width: 100%;
  height: $navigation-bar-height;
  padding: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #007055;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  top: 0;
  left: 0;
  z-index: 1001;
}

.ifield-nav-logo {
  fill: $entity-color-white;
  width: 28px;
  height: 28px;
}

.main-nav {
  width: auto;
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.nav-card {
  a {
    display: flex;
    justify-content: center;
    align-items: center;
    width: auto;
    height: $navigation-bar-height;
    margin: 0;
    padding: 0 30px;
    color: rgba(255, 255, 255, 0.95);
    font-size: 0.9em;
    font-weight: bold;
    text-align: center;
    text-decoration: none !important;
    background-color: #007055;
    border-bottom: 4px solid #007055;
    border-right: 1px solid rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    text-shadow: 0 1px 0 rgba(0, 0, 0, 0.2);
    transition: 0.2s;
    position: relative;

    &:hover {
      color: #ffffff;
      background-color: #108065;
      border-bottom: 4px solid #ffffff;
    }

    img {
      display: block;
      width: 30px;
      height: 30px;
      margin: 15px 0;
    }
  }

  .router-link-active {
    background-color: #015136;
    border-bottom: 4px solid #ffffff;
  }

  .nav-text {
    margin: 0;
  }

  .nav-badge {
    display: block;
    width: auto;
    min-width: 18px;
    height: 18px;
    line-height: 16px;
    margin: 0;
    padding: 0 3px;
    color: #ffffff;
    font-size: 0.6em;
    font-weight: bold;
    text-align: center;
    border: 1px solid #ffffff;
    border-radius: 50%;
    background-color: #ff4100;
    position: absolute;
    top: 6px;
    right: 6px;
    text-align: center;
  }
}
</style>
